import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserSandbox } from './sandbox';
import apiData from '../../services/api_data';

export const postDeleteRecords = createAsyncThunk(
    'date/deleteRows',
    async (_, { getState, dispatch }) => {
        const { tableSelectionModel } = getState().data;
        const data = await apiData.postDeleteRecords(tableSelectionModel);
        return data;
    }
);

export const postUserSandbox = createAsyncThunk(
    'sandbox/post',
    async (_, { getState, dispatch }) => {
        const { tableSelectionModel } = getState().data;
        const data = await apiData.postUserSandbox(tableSelectionModel);

        dispatch(getUserSandbox());
        return data;
    }
);

export const getDataRows = createAsyncThunk(
    'data/getRows',
    async (_, { getState, dispatch }) => {
        dispatch(setLoading(true));
        dispatch(setPaginationMode("server"));

        const dataFilterRaw = sessionStorage.getItem("dataFilter");
        const dataFilter = dataFilterRaw ? JSON.parse(dataFilterRaw) : null;
        if (!dataFilter) {
            dispatch(setLoading(false));
            return { filteredList: [], rowCount: 0 };
        }

        const { page, pageSize, filterModel, sortModel, tabType } = getState().data;
        const { sandboxedItems } = getState().sandbox;

        let filtField = "";
        let filtValue = "";

        const sortExp = sortModel.length > 0
            ? `${sortModel[0].field} ${sortModel[0].sort}`
            : "";

        // MUI X DataGrid v7 filter model: { items: [{ field, operator, value }], ... }.
        // The previous code read `Object.values(filterModel)[0][0].columnField` (v6 shape)
        // which never matched in v7, and arbitrarily required value length > 5.
        // Send the raw user value — the backend uses ILIKE (case-insensitive) for the match.
        const firstItem = Array.isArray(filterModel?.items) ? filterModel.items[0] : null;
        if (firstItem && firstItem.field && firstItem.value != null && firstItem.value !== "") {
            filtField = firstItem.field;
            filtValue = String(firstItem.value);
        }

        // Pass filter object as JSON body (do not stringify – axios serializes the object)
        // `tabType` scopes the query to the active Data tab's Type bucket
        // (advertising/adspend/editorial). Server-side pagination requires this
        // to be applied at the DB; client-side filtering only saw one page at a
        // time and silently produced empty tabs.
        const response = await apiData.getGridData(
            dataFilter,
            page + 1,
            pageSize,
            filtField,
            filtValue,
            sortExp,
            tabType || ""
        );

        // Support both camelCase (local) and PascalCase (some production .NET configs)
        const rowCount = response?.rowCount ?? response?.RowCount ?? 0;
        let filteredList = response?.filteredList ?? response?.FilteredList ?? [];
        if (!Array.isArray(filteredList)) filteredList = [];

        sessionStorage.setItem("rowCount", rowCount);
        const storageEvent = new Event('storage');
        window.dispatchEvent(storageEvent);
        const sandboxedIds = sandboxedItems.map(e => e.id);
        filteredList = filteredList.filter(e => !sandboxedIds.includes(e.id));

        // filteredList.forEach(e => {
        //     if (e.sentiment.includes("*"))
        //         e.sentiment = e.sentiment.replaceAll("*", "");
        // });

        dispatch(setLoading(false));
        return {
            filteredList: filteredList,
            rowCount: rowCount
        };
    }
);

export const dataSlice = createSlice({
    name: 'dataSlice',
    initialState: {
        tableRows: [],
        rowCount: 0,
        tableSelectionModel: [],
        paginationMode: "client",
        filterModel: { items: [] },
        sortModel: [],
        page: 0,
        pageSize: 100,
        loading: true,
        selectionModel: [],
        tabType: "",
    },
    reducers: {
        setTableRows: (state, action) => {
            state.tableRows = action.payload;
        },
        setDataTableSelection: (state, action) => {
            state.tableSelectionModel = action.payload;
        },
        setPaginationMode: (state, action) => {
            state.paginationMode = action.payload;
        },
        setFilterModel: (state, action) => {
            state.filterModel = action.payload;
        },
        setSortModel: (state, action) => {
            state.sortModel = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pageSize = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setSelectionModel: (state, action) => {
            state.selectionModel = action.payload;
        },
        setTabType: (state, action) => {
            state.tabType = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(postUserSandbox.rejected, (_, error) => console.error('REJECTED postUserSandbox: ', error))
        builder.addCase(getDataRows.rejected, (_, error) => console.error('REJECTED getDataRows: ', error))
            .addCase(getDataRows.fulfilled, (state, action) => {
                state.rowCount = action.payload.rowCount;
                state.tableRows = action.payload.filteredList;
            })
    },
});

export const { setTableRows, setDataTableSelection, setPaginationMode, setFilterModel, setSortModel, setPage, setPageSize, setLoading, setSelectionModel, setTabType } = dataSlice.actions;

export default dataSlice.reducer;